/**
 * Link component that wraps react-router-dom's Link
 * Provides API compatibility with next/link
 */
import { Link as RouterLink } from "react-router-dom";
import { forwardRef, type ReactNode, type AnchorHTMLAttributes } from "react";

interface LinkProps
	extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
	href: string;
	children: ReactNode;
	prefetch?: boolean; // Ignored in Vite, kept for Next.js API compatibility
	className?: string;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
	({ href, children, prefetch, ...props }, ref) => {
		// Handle hash links for same-page navigation
		if (href.startsWith("#") || href.startsWith("/#")) {
			const hash = href.startsWith("/#") ? href.slice(1) : href;
			return (
				<a
					ref={ref}
					href={hash}
					onClick={(e) => {
						e.preventDefault();
						const element = document.querySelector(hash);
						if (element) {
							element.scrollIntoView({ behavior: "smooth" });
						}
						// Update URL hash
						window.history.pushState(null, "", hash);
					}}
					{...props}
				>
					{children}
				</a>
			);
		}

		// Handle external links
		if (href.startsWith("http") || href.startsWith("mailto:")) {
			return (
				<a ref={ref} href={href} {...props}>
					{children}
				</a>
			);
		}

		return (
			<RouterLink ref={ref} to={href} {...props}>
				{children}
			</RouterLink>
		);
	},
);

Link.displayName = "Link";
